import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { CheckCircle2, Clock, Mail, MessageSquare, Star } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

interface InstallationCompletedModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerName: string;
  onConfirm: (preferences: {
    sendInstallationEmail: boolean;
  }) => void;
}

export function InstallationCompletedModal({
  open,
  onOpenChange,
  customerName,
  onConfirm,
}: InstallationCompletedModalProps) {
  const [sendInstallationEmail, setSendInstallationEmail] = useState(true);

  const handleConfirm = () => {
    onConfirm({
      sendInstallationEmail,
    });
    onOpenChange(false);
  };

  const handleCancel = () => {
    // Reset to defaults when canceling
    setSendInstallationEmail(true);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-500" />
            Installation Completed
          </DialogTitle>
          <DialogDescription>
            Moving <strong>{customerName}</strong> to Installation Completed stage.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Email Option */}
          <div className="flex items-start space-x-3">
            <Checkbox
              id="sendInstallationEmail"
              checked={sendInstallationEmail}
              onCheckedChange={(checked) => setSendInstallationEmail(checked as boolean)}
            />
            <div className="space-y-1 flex-1">
              <Label
                htmlFor="sendInstallationEmail"
                className="flex items-center gap-2 font-medium cursor-pointer"
              >
                <Mail className="h-4 w-4 text-blue-500" />
                Enable Automated Email Sequence (3 emails)
              </Label>
              <p className="text-xs text-muted-foreground mb-3">
                Triggers the completion email, configured review invite, and feedback survey
              </p>

              {/* Email Schedule Preview */}
              {sendInstallationEmail && (
                <div className="space-y-3">
                  {/* Email 1: Immediate */}
                  <div className="border rounded-lg p-3 bg-green-50/50 dark:bg-green-950/20">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium">Email 1: Installation Complete</span>
                          <span className="text-xs bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 px-2 py-0.5 rounded">
                            Sent immediately
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mb-2">
                          Confirms installation completion and outlines next steps
                        </p>
                        <ScrollArea className="h-[100px] border rounded p-2 bg-white/80 dark:bg-slate-900/80">
                          <div className="text-xs space-y-1">
                            <p className="font-medium">Contents:</p>
                            <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
                              <li>Customer FAQs guide (PDF attached in this email)</li>
                              <li>MCS certificate timeline (14 days)</li>
                              <li>DNO notification with G98/G99 documents</li>
                              <li>Export MPAN request process</li>
                              <li>HIES insurance documentation</li>
                              <li>Handover pack compilation</li>
                              <li>System app tutorial links</li>
                            </ul>
                          </div>
                        </ScrollArea>
                      </div>
                    </div>
                  </div>

                  {/* Email 2: Review Request */}
                  <div className="border rounded-lg p-3 bg-blue-50/50 dark:bg-blue-950/20">
                    <div className="flex items-start gap-2">
                      <Star className="h-4 w-4 text-blue-600 mt-0.5" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium">Email 2: Review Request</span>
                          <span className="text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Day 6-7
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Polite request through the configured review platform. In Trustpilot mode,
                          the invite is triggered through the completion email BCC.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Email 3: Survey */}
                  <div className="border rounded-lg p-3 bg-purple-50/50 dark:bg-purple-950/20">
                    <div className="flex items-start gap-2">
                      <MessageSquare className="h-4 w-4 text-purple-600 mt-0.5" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium">Email 3: Customer Feedback Survey</span>
                          <span className="text-xs bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Day 14
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          60-second JotForm survey to gather detailed feedback about the installation experience.
                          Helps improve service quality and customer satisfaction.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Additional Information */}
          <div className="rounded-lg border bg-amber-50/50 dark:bg-amber-950/20 p-3">
            <p className="text-xs text-amber-700 dark:text-amber-300 flex items-start gap-2">
              <Clock className="h-3 w-3 mt-0.5 flex-shrink-0" />
              <span>
                <strong>Automated Schedule:</strong> When enabled, all 3 emails are sent automatically at the specified times.
                The system tracks review requests to prevent duplicates. Customers can reply to any DomElec email for direct communication.
              </span>
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} className="bg-green-600 hover:bg-green-700">
            Confirm & Complete Installation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
